import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useLoginMutation,
  useRegisterMutation,
  useLogoutMutation,
  useGetMeQuery,
  useVerify2FAMutation,
} from '@api/authApi';
import { setAuthenticated, setActiveWorkspace, clearAuth } from '@store/authSlice';
import { disconnectSocket } from './useSocket';

export const useAuth = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get current user (skip if no token stored)
  const hasToken = !!localStorage.getItem('accessToken');
  const {
    data: userData,
    isLoading: isLoadingUser,
    error: userError,
    refetch: refetchUser,
  } = useGetMeQuery(undefined, { skip: !hasToken });

  const user = userData?.data || null;
  const isAuthenticated = !!user;

  // Mutations
  const [loginMutation, { isLoading: isLoggingIn }] = useLoginMutation();
  const [registerMutation, { isLoading: isRegistering }] = useRegisterMutation();
  const [verify2FAMutation, { isLoading: isVerifying2FA }] = useVerify2FAMutation();
  const [logoutMutation] = useLogoutMutation();

  // Helper to store tokens and update Redux state
  const storeAuthTokens = useCallback((data) => {
    const { accessToken, refreshToken, user: loggedInUser } = data;
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
    const wsId = loggedInUser?.activeWorkspaceId || loggedInUser?.workspaceIds?.[0];
    if (wsId) {
      localStorage.setItem('activeWorkspaceId', wsId);
      dispatch(setActiveWorkspace(wsId));
    }
    dispatch(setAuthenticated(true));
  }, [dispatch]);

  // ── Login ──
  const login = useCallback(async (credentials) => {
    const result = await loginMutation(credentials).unwrap();

    if (result.data?.requires2FA) {
      return { requires2FA: true, tempToken: result.data.tempToken };
    }

    storeAuthTokens(result.data);
    try { refetchUser(); } catch {}
    navigate('/');
    return result;
  }, [loginMutation, storeAuthTokens, refetchUser, navigate]);

  // ── Verify 2FA (supports totpCode OR backupCode) ──
  const verify2FA = useCallback(async ({ tempToken, totpCode, backupCode }) => {
    const result = await verify2FAMutation({ tempToken, totpCode, backupCode }).unwrap();
    storeAuthTokens(result.data);
    try { refetchUser(); } catch {}
    navigate('/');
    return result;
  }, [verify2FAMutation, storeAuthTokens, refetchUser, navigate]);

  // ── Register ──
  const register = useCallback(async (data) => {
    const result = await registerMutation(data).unwrap();
    storeAuthTokens(result.data);
    try { refetchUser(); } catch {}
    navigate('/');
    return result;
  }, [registerMutation, storeAuthTokens, refetchUser, navigate]);

  // ── Logout ──
  const logout = useCallback(async () => {
    try { await logoutMutation().unwrap(); } catch {}
    dispatch(clearAuth());
    disconnectSocket();
    navigate('/login');
  }, [logoutMutation, dispatch, navigate]);

  return useMemo(() => ({
    user,
    isAuthenticated,
    isLoading: isLoadingUser,
    isLoggingIn,
    isRegistering,
    isVerifying2FA,
    error: userError,
    login,
    verify2FA,
    register,
    logout,
    refetchUser,
  }), [user, isAuthenticated, isLoadingUser, isLoggingIn, isRegistering, isVerifying2FA, userError, login, verify2FA, register, logout, refetchUser]);
};
